// =============================================================================
// App shell and routing.
//
// One codebase, one URL, two shells selected by VIEWPORT WIDTH — never by
// device sniffing and never by a separate route (§2.2). In the walking skeleton
// only the transaction screens exist, so both shells show the same two tabs;
// the desktop shell grows the remaining screens later.
// =============================================================================

import './styles.css';
import { el, mount, logoImg } from './lib/dom.js';
import { T } from './lib/strings.js';
import { SCHOOL, fullAppName } from './config/school.js';
import { applyTheme } from './lib/theme.js';
import { loadStaff, signOut, getStaff, onAuthStateChange } from './lib/auth.js';
import { MOCK } from './lib/api.js';
import { loginScreen } from './screens/login.js';
import { issueScreen } from './screens/issue.js';
import { returnScreen } from './screens/return.js';
import { addBooksScreen } from './screens/add-books.js';
import { booksScreen } from './screens/books.js';
import { overdueScreen } from './screens/overdue.js';
import { settingsScreen } from './screens/settings.js';
import { countOverdue } from './lib/api.js';
import { onDataChanged } from './lib/events.js';
import { membersScreen } from './screens/members.js';
import { paymentsScreen } from './screens/payments.js';
import { spinner, mountStallBar } from './lib/loading.js';
import { memberDetailScreen } from './screens/member-detail.js';
import { memberFormScreen } from './screens/member-form.js';

// Palette is DERIVED from SCHOOL.brand, not hardcoded — see lib/theme.js for
// why (brand colours are chosen for signage, not for text contrast).
applyTheme();
document.title = fullAppName();

const app = document.getElementById('app');
let active = null;   // the mounted screen, so its scanner can be stopped

// Every screen is reachable from every shell.
//
// §2.2 originally hid the wider screens from the phone tab bar. In practice
// that just made staff think features were missing — "I do not see books" was
// the first feedback. Hiding a tab does not make a small screen bigger; it only
// makes the app look broken. The tab bar scrolls instead.
const ROUTES = {
  issue:   { label: T.navIssue,   render: issueScreen,  phone: true },
  return:  { label: T.navReturn,  render: returnScreen, phone: true },
  members: { label: T.navMembers, render: membersScreen, phone: true },
  // Money in one place. A payment was previously only visible from the member
  // it belonged to, which cannot answer "what came in today?" — the question
  // the paper receipt book gets reconciled against.
  payments: { label: T.navPayments, render: paymentsScreen, desktopOnly: true },
  // §7 calls this "the reminder mechanism" — with no automated reminders in v1
  // it is the only way a late book comes back, so it is on the phone.
  overdue: { label: T.navOverdue, render: overdueScreen, badge: true, phone: true },
  books:   { label: T.navBooks,   render: booksScreen, desktopOnly: true },
  // Reachable by direct link; adding books lives inside Books.
  add:     { label: T.navAdd,     render: addBooksScreen, hidden: true, desktopOnly: true },
  // Open to front desk too: they manage their own branch's fee and borrowing
  // rules. The staff-accounts section inside stays admin-only.
  settings: { label: T.navSettings, render: settingsScreen, desktopOnly: true },
};

// Hash routes, with the one parameter shape this app needs:
//   #/members            list
//   #/members/new        create
//   #/members/<id>       detail
//   #/members/<id>/edit  edit
//   #/members/<id>/pay   detail, fee form open (where "Create member" lands)
//   #/issue/<id>         issue, with that member already chosen
function parseRoute() {
  const parts = location.hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  const [name, param, action] = parts;
  if (!ROUTES[name]) return { name: 'issue', render: issueScreen };

  // Arriving from a member's screen, where the student is already known. The
  // search step is skipped rather than repeated.
  if (name === 'issue' && param) {
    return { name, render: (r) => issueScreen(r, param) };
  }

  if (name === 'members' && param) {
    if (param === 'new') return { name, render: (r) => memberFormScreen(r, 'new') };
    if (action === 'edit') return { name, render: (r) => memberFormScreen(r, param) };
    // #/members/<id>/pay — where creating a member lands. Same detail screen,
    // with the fee form already open, so signing a child up and taking the
    // money is one flow rather than a create followed by a hunt for a button.
    return { name, render: (r) => memberDetailScreen(r, param, { pay: action === 'pay' }) };
  }
  return { name, render: ROUTES[name].render };
}

async function renderApp() {
  const staff = getStaff();
  if (!staff) return renderLogin();

  const route = parseRoute();
  // Transaction screens stay in a readable column; everything else gets the
  // full width. A 720px column on a 1440px monitor reads as a broken layout,
  // which is exactly what it was reported as.
  const narrow = route.name === 'issue' || route.name === 'return';
  const content = el('main', { class: `content ${narrow ? 'content-narrow' : ''}` });

  const nav = buildNav(route);

  mount(app,
    el('header', { class: 'topbar' },
      logoImg(SCHOOL.brand.mark || SCHOOL.brand.logo, 'topbar-logo'),
      // Which branch, beside the name of the app. Three branches otherwise see
      // an identical screen, and everything below it — the catalogue, the
      // members, the overdue list — is scoped to one of them.
      el('div', { class: 'topbar-names' },
        el('span', { class: 'topbar-title' }, SCHOOL.appName),
        el('span', { class: 'topbar-branch' },
          staff.branch_name ?? (staff.role === 'admin' ? T.allBranches : '')),
      ),
      el('span', { class: 'topbar-user' }, staff.full_name),
      el('button', { class: 'btn btn-link', onClick: doSignOut }, T.signOut),
    ),
    MOCK && !demoClean ? el('div', { class: 'mock-bar' }, T.mockMode) : null,
    content,
    nav,
  );

  // Stop any running camera before swapping screens — a scanner left running
  // holds the camera and drains the battery for the rest of the shift.
  await active?.destroy?.();

  // Issue, Return, Members, Member detail and Overdue are the phone. The rest
  // are wide tables — Books is eight columns, Payments is nine — and a table
  // read two columns at a time through a 375px window is not a screen anyone
  // can work from. They stay reachable, and the tab stays visible: hiding tabs
  // was tried and read as missing features.
  //
  // Not a block. "Show it anyway" is there because someone standing at the
  // shelf will need to look up one title, and being told no by their own app
  // is worse than a table they have to scroll.
  if (ROUTES[route.name]?.desktopOnly && !isDesktop() && !showNarrowAnyway.has(route.name)) {
    mount(content, desktopOnlyNotice(route.name));
    refreshOverdueBadge(nav);
    return;
  }

  active = route.render(content);

  refreshOverdueBadge(nav);
}

function tabLink(key, r, route) {
  return el('a', {
    href: `#/${key}`,
    class: `tab ${key === route.name ? 'tab-active' : ''}`,
    'aria-current': key === route.name ? 'page' : null,
  },
    r.label,
    // The badge is what makes this screen get looked at. Without a number
    // on the tab, an overdue list is a page nobody opens.
    r.badge ? el('span', { class: 'tab-badge hidden' }) : null,
  );
}

// The tab bar used to scroll sideways: eight tabs at 5.5rem each is 637px in a
// 375px window. Scrolling was chosen over hiding tabs because staff had
// reported missing tabs as missing features — but a bar you have to drag is
// its own way of hiding something, and it was the last thing on the phone that
// moved horizontally.
//
// So the phone gets its four screens and a More button. Nothing is hidden:
// More names the other four out loud, in a sheet, at full tap size.
function buildNav(route) {
  const entries = Object.entries(ROUTES).filter(([, r]) => !r.hidden);
  if (isDesktop()) {
    return el('nav', { class: 'tabbar' },
      ...entries.map(([key, r]) => tabLink(key, r, route)));
  }

  const phone = entries.filter(([, r]) => r.phone);
  const rest  = entries.filter(([, r]) => !r.phone);
  // If the reader is on one of the other screens, More is the current tab —
  // otherwise the bar would show nothing selected and read as lost.
  const restActive = rest.some(([key]) => key === route.name);

  return el('nav', { class: 'tabbar' },
    ...phone.map(([key, r]) => tabLink(key, r, route)),
    el('button', {
      class: `tab tab-more ${restActive ? 'tab-active' : ''}`,
      'aria-haspopup': 'true', 'aria-expanded': 'false',
      onClick: () => openMoreSheet(route, rest),
    }, T.navMore),
  );
}

// A sheet, not a dropdown: it comes up from the bar it was tapped on, every
// row is a full-width tap target, and the scrim closes it. Same scrim and the
// same animation the Books panel already uses.
function openMoreSheet(route, rest) {
  // Where the thumb came from, so Escape puts focus back on the button that
  // opened the sheet rather than dropping it on <body>.
  const opener = document.activeElement;
  const moreTab = document.querySelector('.tab-more');

  const close = () => {
    scrim.remove(); sheet.remove();
    document.removeEventListener('keydown', onKey);
    // The page scrolled behind the sheet. body.scanner-open is the lock the
    // scanner overlay already uses; this is the same job.
    document.body.classList.remove('scanner-open');
    moreTab?.setAttribute('aria-expanded', 'false');
    if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
  };
  const onKey = (e) => { if (e.key === 'Escape') close(); };

  const scrim = el('div', { class: 'panel-scrim', onClick: close });
  const sheet = el('nav', { class: 'more-sheet', role: 'menu', 'aria-label': T.navMore },
    el('p', { class: 'more-sheet-title' }, T.navMore),
    ...rest.map(([key, r]) => el('a', {
      href: `#/${key}`, role: 'menuitem',
      class: `more-item ${key === route.name ? 'more-item-active' : ''}`,
      onClick: close,
    }, r.label)),
  );

  document.body.append(scrim, sheet);
  document.body.classList.add('scanner-open');
  moreTab?.setAttribute('aria-expanded', 'true');
  document.addEventListener('keydown', onKey);
  queueMicrotask(() => sheet.querySelector('.more-item')?.focus());
}

// Routes the reader has explicitly asked to see on a narrow screen. Held for
// the session only: it is an override, not a preference worth remembering.
const showNarrowAnyway = new Set();
const isDesktop = () => window.matchMedia('(min-width: 768px)').matches;

function desktopOnlyNotice(name) {
  return el('div', {},
    // Without this the four gated screens were the only ones in the app with
    // no <h1>: tapping Books gave a card that never said "Books".
    el('h1', { class: 'screen-title' }, ROUTES[name].label),
    el('div', { class: 'empty desktop-only' },
    el('p', { class: 'card-title' }, T.desktopOnlyTitle),
    el('p', {}, T.desktopOnlyHint),
    el('button', {
      class: 'btn',
      onClick: () => { showNarrowAnyway.add(name); renderApp(); },
    }, T.desktopOnlyAnyway),
  ));
}

function renderLogin() {
  active = null;
  loginScreen(app, () => { location.hash = '#/issue'; renderApp(); });
}

async function doSignOut() {
  await active?.destroy?.();
  await signOut();
  renderLogin();
}

window.addEventListener('hashchange', renderApp);
onDataChanged(() => refreshOverdueBadge());

// Crossing the shell breakpoint changes which tabs exist, so re-render rather
// than leaving a stale nav after a rotation or a window resize.
let wasDesktop = window.matchMedia('(min-width: 768px)').matches;
window.addEventListener('resize', () => {
  const now = window.matchMedia('(min-width: 768px)').matches;
  if (now !== wasDesktop) { wasDesktop = now; renderApp(); }
});

// Refreshed on navigation AND whenever a transaction announces itself.
//
// Navigation alone is not enough: returning a book re-renders only the screen,
// not the shell, so the badge would sit there stale — saying two books are
// overdue while the desk has just taken both back.
//
// A failure here must never break navigation, so it fails silently.
async function refreshOverdueBadge(nav) {
  const badge = (nav ?? document).querySelector('.tab-badge');
  if (!badge) return;
  try {
    const n = await countOverdue();
    badge.textContent = n > 99 ? '99+' : String(n);
    badge.classList.toggle('hidden', !n);
  } catch {
    badge.classList.add('hidden');
  }
}

// Connectivity banner. There is no offline mode (§11) — the honest thing is to
// tell staff immediately rather than let a transaction fail mysteriously.
const offlineBar = el('div', { class: 'offline-bar hidden' }, T.offline);
document.body.append(offlineBar);
const syncOnline = () => offlineBar.classList.toggle('hidden', navigator.onLine);
window.addEventListener('online', syncOnline);
window.addEventListener('offline', syncOnline);
syncOnline();

// Offer the new version rather than reloading underneath a half-finished
// transaction. See lib/updates.js.
import('./lib/updates.js').then((m) => m.mountUpdatePrompt()).catch(() => {});

// A request that has stopped moving gets a way out, in the corner, rather than
// leaving staff looking at a motionless screen wondering whether to reload.
mountStallBar(() => renderApp());

// DEMO MODE ONLY: ?demo=admin or ?demo=frontdesk signs straight in, so a
// walkthrough, a screenshot run or a training session can open a screen
// directly instead of typing a password first.
//
// Guarded by MOCK, which is VITE_MOCK — never set on either school's Cloudflare
// project. Against a real database this branch cannot run: there is no such
// thing as signing in without a password, and nothing here creates one.
// Set by ?clean=1 alongside ?demo=. Hides the demo banner for screenshots: a
// training guide should show staff the screen they will actually see, not a
// warning strip that exists only in fixtures.
let demoClean = false;

async function demoAutoSignIn() {
  if (!MOCK) return false;
  const params = new URLSearchParams(location.search);
  demoClean = params.get('clean') === '1';
  const role = params.get('demo');
  if (role !== 'admin' && role !== 'frontdesk') return false;
  const { signIn } = await import('./lib/auth.js');
  // The mock reads the role off the email; the password is ignored.
  await signIn(role === 'admin' ? 'admin@example.com' : 'frontdesk@example.com', 'demo');
  return true;
}

(async function boot() {
  mount(app, spinner());
  await loadStaff();
  await demoAutoSignIn();
  await renderApp();
})();

// Session may expire or be signed out in another tab.
onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') { active = null; renderLogin(); }
});
